import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/authSlice';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

const GET_USER_SUBSCRIPTION = gql`
  query GetUserSubscription {
    mySubscriptions {
      id
      status
      starts_at
      expires_at
      package {
        id
        name
        max_staff_members
        max_contacts
        max_whatsapp_instances
        package_type
        price
        currency
      }
    }
  }
`;

const Sidebar = ({
  activeSection,
  onSectionChange,
  isCollapsed,
  onToggleCollapse,
  isMobileMenuOpen,
  onCloseMobileMenu
}) => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get user subscription data for package restrictions
  const { data: subscriptionData } = useQuery(GET_USER_SUBSCRIPTION, {
    skip: user?.role !== 'user_admin',
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Helper function to check if user can access staff management
  const canAccessStaffManagement = () => {
    if (user?.role !== 'user_admin') return false;

    if (!subscriptionData?.mySubscriptions) return false;

    const activeSubscription = subscriptionData.mySubscriptions.find(sub => sub.status === 'active');
    if (!activeSubscription) return false;

    const maxStaff = activeSubscription.package?.max_staff_members || 0;
    return maxStaff > 0;
  };

  // Helper function to check if user can access contact management
  const canAccessContactManagement = () => {
    if (user?.role !== 'user_admin') return false;

    if (!subscriptionData?.mySubscriptions) return false;

    const activeSubscription = subscriptionData.mySubscriptions.find(sub => sub.status === 'active');
    if (!activeSubscription) return false;

    const maxContacts = activeSubscription.package?.max_contacts || 0;
    return maxContacts > 0;
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const sidebarItems = [
    { id: 'dashboard', icon: 'grid_view', label: 'Dashboard' },
    { id: 'whatsapp', icon: 'phonelink_ring', label: 'Whatapp Instance' },
    { id: 'bulk-messaging', icon: 'send', label: 'Message Template' },
    { id: 'contacts', icon: 'person', label: 'Contacts' },
    { id: 'contact-management', icon: 'contacts', label: 'Contact Management' },
    { id: 'contact-groups', icon: 'group', label: 'Groups' },
    { id: 'blocked-users', icon: 'block', label: 'Block User' },
    { id: 'lid-mappings', icon: 'link', label: 'ID Resolution' },
    { id: 'user-management', icon: 'manage_accounts', label: 'Access Control' },
    { id: 'staff-management', icon: 'group_add', label: 'Staff Management' },
    { id: 'package-management', icon: 'inventory_2', label: 'Package Management' },
    { id: 'total-earnings', icon: 'trending_up', label: 'Earnings' },
    { id: 'order-management', icon: 'receipt_long', label: 'Order Management' },
    { id: 'super-admin-management', icon: 'admin_panel_settings', label: 'Profile' },
  ];

  // Filter sidebar items based on user role
  const getFilteredSidebarItems = () => {
    if (user?.role === 'user_admin') {
      // User admin: Dashboard, Contact Management (if package allows), Staff Management (if package allows), Order Management, and Profile
      let restrictedItems = sidebarItems.filter(item =>
        item.id === 'dashboard' ||
        item.id === 'order-management' ||
        item.id === 'super-admin-management'
      );

      // Only add contact management if package allows it
      if (canAccessContactManagement()) {
        const contactManagementItem = sidebarItems.find(item => item.id === 'contact-management');
        if (contactManagementItem) {
          restrictedItems.splice(1, 0, contactManagementItem); // Insert after dashboard
        }
      }

      // Only add staff management if package allows it
      if (canAccessStaffManagement()) {
        const staffManagementItem = sidebarItems.find(item => item.id === 'staff-management');
        if (staffManagementItem) {
          const insertIndex = canAccessContactManagement() ? 2 : 1; // After contact management or dashboard
          restrictedItems.splice(insertIndex, 0, staffManagementItem);
        }
      }

      console.log(`🔒 Role-based access: ${user.role} has access to:`, restrictedItems.map(item => item.label));
      return restrictedItems;
    } else if (user?.role === 'user_staff') {
      // User staff: only Dashboard and Profile (NO staff management, contact management, or order management)
      const restrictedItems = sidebarItems.filter(item =>
        item.id === 'dashboard' || item.id === 'super-admin-management'
      );
      console.log(`🔒 Role-based access: ${user.role} has access to:`, restrictedItems.map(item => item.label));
      return restrictedItems;
    }
    // Super admin gets all items except order-management, contacts, contact-management, and staff-management (hidden as per requirement)
    const superAdminItems = sidebarItems.filter(item => 
      item.id !== 'order-management' &&
      // Hiding these items for super admin as per requirement
      item.id !== 'contacts' &&
      item.id !== 'contact-management' &&
      item.id !== 'staff-management'
    );
    console.log(`🔓 Full access: ${user?.role} has access to all features`);
    return superAdminItems;
  };

  const filteredSidebarItems = getFilteredSidebarItems();

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-300"
          onClick={onCloseMobileMenu}
        ></div>
      )}

      {/* Heavy Duty Sidebar */}
      <div
        className={`bg-gradient-to-b from-bulky-navy-800 to-bulky-navy-950 text-white flex flex-col shadow-2xl z-50 fixed lg:relative h-full transition-all duration-500 ease-in-out overflow-visible
          ${isMobileMenuOpen ? 'left-0' : '-left-full lg:left-0'}
          ${isCollapsed ? 'lg:w-24' : 'lg:w-[320px] w-72'}
        `}
      >
        {/* Collapse Toggle Control (Desktop Only) */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-24 bg-bulky-blue-500 text-white w-6 h-12 rounded-full items-center justify-center shadow-xl hover:bg-bulky-blue-600 transition-all z-50 group border border-white/10"
        >
          <span className={`material-icons-round text-sm transition-transform duration-500 ${isCollapsed ? '' : 'rotate-180'}`}>chevron_right</span>
        </button>

        {/* Background Decorative Gradient */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-r-[2.5rem] lg:rounded-none">
          <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-bulky-blue-500/10 to-transparent"></div>
        </div>

        <div className={`p-6 md:p-8 mb-4 relative z-10 border-b border-white/5 transition-all duration-500 ${isCollapsed ? 'lg:px-6' : 'px-6 md:px-8'}`}>
          <div className="flex items-center gap-4">
            <div className={`bg-white p-2 md:p-2.5 rounded-2xl shadow-xl shadow-black/20 transform -rotate-3 hover:rotate-0 transition-all duration-500 shrink-0 ${isCollapsed ? 'lg:scale-90 lg:rotate-0' : ''}`}>
              <img
                src="/bm-logo.png"
                alt="Bulky Logo"
                className="h-8 md:h-9 w-8 md:w-9 object-contain"
              />
            </div>
            <div className={`flex flex-col transition-all duration-500 ${isCollapsed ? 'lg:hidden opacity-0' : 'opacity-100'}`}>
              <h2 className="text-lg font-semibold tracking-tight text-white">BULKY</h2>
              <span className="text-[11px] font-medium text-white tracking-wider mt-1">Marketing OS</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto space-y-2 custom-scrollbar relative z-10 px-4">
          {!isCollapsed && (
            <p className="px-4 text-[11px] font-medium text-white/60 tracking-wider mb-4 mt-2 opacity-100 animate-in fade-in duration-500">
              Operational Layers
            </p>
          )}
          {filteredSidebarItems.map(item => (
            <div key={item.id} className="relative group/nav">
              <button
                className={`w-full flex items-center rounded-xl transition-all duration-500 group relative ${isCollapsed ? 'lg:justify-center py-4' : 'gap-3 px-4 py-3'
                  } ${activeSection === item.id
                    ? 'bg-bulky-blue-500 shadow-[0_10px_30px_-10px_rgba(40,92,237,0.5)] text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                onClick={() => {
                  onSectionChange(item.id);
                  onCloseMobileMenu();
                }}
              >
                {activeSection === item.id && !isCollapsed && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-white rounded-l-full"></div>
                )}
                <span className={`material-icons-round text-lg transition-all duration-500 ${activeSection === item.id ? 'lg:scale-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'group-hover:scale-110 opacity-60 group-hover:opacity-100'
                  }`}>{item.icon}</span>
                <span className={`font-medium text-[13px] tracking-normal transition-all duration-500 ${isCollapsed ? 'lg:hidden opacity-0' : 'opacity-100'}`}>
                  {item.label}
                </span>
              </button>
            </div>
          ))}
        </nav>

        <div className={`mt-auto relative z-10 transition-all duration-500 border-t border-white/5 ${isCollapsed ? 'lg:p-4' : 'p-6 md:p-8'}`}>
          <button
            onClick={handleLogout}
            className={`flex items-center justify-center bg-white/5 hover:bg-red-500 hover:text-white text-bulky-navy-400 font-normal rounded-xl transition-all duration-500 border border-white/5 group text-[11px] tracking-wider ${isCollapsed ? 'lg:w-14 lg:h-14' : 'w-full py-4 px-4 gap-3'
              }`}
          >
            <span className="material-icons-round text-lg group-hover:rotate-180 transition-transform duration-700">logout</span>
            <span className={`transition-all duration-500 text-[11px] text-white ${isCollapsed ? 'lg:hidden opacity-0' : 'opacity-100'}`}>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;